import { isNotFoundError } from "@/api/utils/error";
import type { CompanyV2 } from "@/domains/company/company/api/types";

/**
 * The four screens the sub-sheet can be in, decided in one place.
 */
export type CompanyInfoState =
  | { kind: "unavailable" }
  | { kind: "loading" }
  | { kind: "error"; retryable: boolean }
  | { kind: "ready"; company: CompanyV2 };

/**
 * The slice of the company query this decision actually reads.
 *
 * Structural and minimal on purpose: the real `useGetCompanyV2(...)` result is
 * assignable to it, and a plain object literal is too — which is what lets the
 * whole decision be tested in a node `.test.ts` without mounting TanStack.
 */
export type CompanyInfoQueryLike = {
  status: "pending" | "error" | "success";
  error?: unknown;
  data?: { company?: CompanyV2 | null } | undefined;
};

export type ResolveCompanyInfoStateInput = {
  companyId: string;
  query: CompanyInfoQueryLike;
};

/**
 * The shape the id is read out of, structural for the same reason as
 * `CompanyInfoQueryLike`: `JobOfferV2WithCompanyId` is assignable to it, and so
 * is an object literal, so the decision keeps its node test.
 *
 * `company_id` is optional **and** nullable here on purpose, against a FE type
 * that declares it neither. The server's is `CompanyPopulated | null` and it is
 * filled by a left join, which drizzle nulls out as a whole object when the
 * join finds nothing — so an offer whose company row is gone arrives with the
 * field at `null`, and the FE type saying otherwise only means the compiler
 * cannot warn about it.
 */
export type CompanyIdSource = {
  company_id?: { id?: string | null } | null;
};

/**
 * The id the company query is asked for, or `""` when there is none to ask.
 *
 * Two ways it comes out empty, and they are not the same situation. A
 * solo-freelancer brand disables the query deliberately — that offer has a
 * person behind it, not a company, and the surface renders a tooltip rather
 * than this sheet. An **orphan offer** is the other: the company row is gone,
 * the left join came back empty, and the field is `null`. Both must produce the
 * unavailability screen (AC 16, FLOW EP4), which is why they collapse to the
 * same empty string here instead of being told apart downstream.
 *
 * Reading `job.company_id.id` unguarded is what this exists to stop: it throws
 * during `DetailContent`'s setup, so the failure is not a sub-sheet that cannot
 * load — it is the whole offer detail failing to open.
 */
export const companyQueryId = (
  job: CompanyIdSource,
  isSoloFreelancer: boolean,
): string => (isSoloFreelancer ? "" : (job.company_id?.id ?? ""));

/**
 * Which of the four screens the sub-sheet owes the user.
 *
 * The order of the branches is the whole point, and it is not the order the
 * query's own flags suggest.
 *
 * **The id comes first.** `createCompanyQueryConfig` carries
 * `enabled: !!companyId`, and in TanStack Query v5 a query that never runs is
 * not "idle": it reads `status: "pending"` forever. Any `isPending → loading`
 * branch written above this check turns an offer with no company id — a
 * solo-freelancer brand, an orphan offer, a malformed preview — into a skeleton
 * that spins for the rest of the session (FLOW EP4). The call site really does
 * produce that input: `detail-sheet.tsx` passes `""` for solo-freelancer.
 *
 * **A company we already hold beats an error.** TanStack keeps `data` when it
 * dispatches `error`, so `{status: "error", data: {company}}` is a real state,
 * not a contradiction — and it is a common one here: the client leaves
 * `staleTime: 0` and `refetchOnMount: true`, so every offer opened refetches a
 * company already in cache, and the bare `[COMPANY_KEY]` prefix is invalidated
 * from a dozen places. Letting the error branch win would replace a company the
 * user is reading with «we could not load this company» because a *background*
 * request failed — announcing a breakage about data that is still on screen,
 * which is the one thing the copy split was made to avoid. A failed refresh of
 * something we have is not an unavailability.
 *
 * A 404 is separated from everything else because the *offer* differs, not the
 * message: there is nothing to ask again, so no «Riprova» (EP3 vs EP2).
 *
 * A 200 carrying no company is **not** a wait either. It is an answer, an empty
 * one, so it lands on the same unavailability screen rather than keeping the
 * skeleton alive on a request that has already come back.
 */
export const resolveCompanyInfoState = (
  input: ResolveCompanyInfoStateInput,
): CompanyInfoState => {
  if (!input.companyId) return { kind: "unavailable" };

  const { query } = input;

  const company = query.data?.company;
  if (company) return { kind: "ready", company };

  if (query.status === "error") {
    return { kind: "error", retryable: !isNotFoundError(query.error) };
  }

  // Reached only with no company in hand: a 200 that carried none.
  if (query.status === "success") return { kind: "unavailable" };

  return { kind: "loading" };
};

/** The screen a state owes the user. Four states, three screens. */
export type CompanyInfoScreen = "skeleton" | "retry" | "unavailable" | "sheet";

/**
 * Which screen renders, as a value rather than as a condition inside JSX.
 *
 * The mapping is not one-to-one, and the place it collapses is the one worth
 * protecting: **only a retryable failure gets the «Riprova» button.** A 404 is
 * also `kind: "error"`, and it lands on the unavailability screen with the
 * missing-id case, because in both the user can do nothing and a button that
 * asks the same question again would lie about that (EP3 vs EP2).
 *
 * That collapse used to live in the `<Switch>` as a memo named after the
 * button's condition, where nothing could reach it: the resolver had its own
 * node test and the three screens had theirs, and the wiring between them had
 * neither. Swapping that memo for `kind === "error"` — a plausible tidy-up —
 * put «Riprova» on a 404 with the whole suite still green. Here the same
 * decision is a pure function over the four states, so the case that must not
 * regress is enumerable, and what is left in the JSX is one `<Match>` per
 * value.
 */
export const companyInfoScreen = (
  state: CompanyInfoState,
): CompanyInfoScreen => {
  switch (state.kind) {
    case "loading":
      return "skeleton";
    case "error":
      return state.retryable ? "retry" : "unavailable";
    case "ready":
      return "sheet";
    case "unavailable":
      return "unavailable";
  }
};
